import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { format, subWeeks, startOfWeek, endOfWeek } from 'date-fns';

function SkeletonBlock({ className }) {
  return <div className={`bg-[var(--color-muted)] animate-pulse rounded ${className}`} />;
}

const EXERCISE_COLORS = ['#0F766E', '#0D9488', '#EA580C', '#D97706', '#059669', '#7C3AED'];

export default function WorkoutStats({ memberId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      setLogs([]);
    } catch (e) {
      setError(e?.message ?? 'Failed to load workout data');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Compute stats
  const totalCompleted = logs?.length ?? 0;

  // Weekly chart: last 8 weeks
  const now = new Date();
  const weeklyData = Array.from({ length: 8 }, (_, i) => {
    const weekStart = startOfWeek(subWeeks(now, 7 - i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(subWeeks(now, 7 - i), { weekStartsOn: 1 });
    const completed = logs?.filter(l => {
      const d = new Date(l?.completed_at);
      return d >= weekStart && d <= weekEnd;
    })?.length ?? 0;
    return {
      week: format(weekStart, 'MMM d'),
      completed,
      target: 4,
    };
  });

  // Streak calculation
  const sortedDates = [...new Set(logs?.map(l => format(new Date(l?.completed_at), 'yyyy-MM-dd')))]?.sort()?.reverse();
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  const today = format(now, 'yyyy-MM-dd');
  const yesterday = format(new Date(now.getTime() - 86400000), 'yyyy-MM-dd');

  if (sortedDates?.length > 0) {
    let prev = null;
    for (let i = 0; i < sortedDates?.length; i++) {
      const d = sortedDates?.[i];
      if (i === 0) {
        if (d === today || d === yesterday) {
          currentStreak = 1;
          tempStreak = 1;
        }
      } else {
        const prevDate = new Date(prev);
        const currDate = new Date(d);
        const diff = Math.round((prevDate - currDate) / 86400000);
        if (diff === 1) {
          tempStreak++;
          if (i < currentStreak + 1 && (sortedDates?.[0] === today || sortedDates?.[0] === yesterday)) {
            currentStreak = tempStreak;
          }
        } else {
          if (tempStreak > longestStreak) longestStreak = tempStreak;
          tempStreak = 1;
        }
      }
      prev = d;
    }
    if (tempStreak > longestStreak) longestStreak = tempStreak;
    if (currentStreak === 0 && sortedDates?.[0] !== today && sortedDates?.[0] !== yesterday) currentStreak = 0;
  }

  // Exercise type breakdown
  const typeMap = {};
  logs?.forEach(l => {
    const t = l?.exercise_type ?? 'Other';
    typeMap[t] = (typeMap?.[t] ?? 0) + 1;
  });
  const typeData = Object.entries(typeMap)?.map(([name, value]) => ({ name, value }))?.sort((a, b) => b?.value - a?.value);

  // Completion rate (vs target 4/week)
  const totalTargetWeeks = 8 * 4;
  const completionRate = totalTargetWeeks > 0 ? Math.min(100, Math.round((totalCompleted / totalTargetWeeks) * 100)) : 0;

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4]?.map(i => <SkeletonBlock key={i} className="h-48" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
        <Icon name="AlertCircle" size={16} color="var(--color-error)" />
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
      </div>
    );
  }

  if (logs?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)]">
        <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
          <Icon name="Dumbbell" size={28} color="var(--color-muted-foreground)" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">No workout logs yet</h3>
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
            Start logging your workouts to see your progress here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Completion Ring */}
        <div className="col-span-2 lg:col-span-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)] flex flex-col items-center justify-center gap-3">
          <div className="relative w-28 h-28">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--color-muted)" strokeWidth="10" />
              <circle
                cx="50" cy="50" r="40" fill="none"
                stroke="var(--color-primary)" strokeWidth="10"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - completionRate / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{completionRate}%</span>
              <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">completion</span>
            </div>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center">
            {totalCompleted} workouts in 8 weeks
          </p>
        </div>

        {/* Total Workouts */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
            </div>
            <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Total Completed</span>
          </div>
          <p className="text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{totalCompleted}</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">all time</p>
        </div>

        {/* Current Streak */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-accent)]/10 flex items-center justify-center">
              <Icon name="Flame" size={16} color="var(--color-accent)" />
            </div>
            <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Current Streak</span>
          </div>
          <p className="text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{currentStreak}</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">days · best: {longestStreak}</p>
        </div>

        {/* Top Exercise Type */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-success)]/10 flex items-center justify-center">
              <Icon name="Star" size={16} color="var(--color-success)" />
            </div>
            <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Top Type</span>
          </div>
          <p className="text-xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)] truncate">{typeData?.[0]?.name ?? '—'}</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">{typeData?.[0]?.value ?? 0} sessions</p>
        </div>
      </div>
      {/* Weekly Bar Chart */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Weekly Workout Activity (Last 8 Weeks)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={weeklyData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }} />
            <Tooltip
              contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-caption)' }}
              labelStyle={{ color: 'var(--color-foreground)', fontWeight: 600 }}
            />
            <Bar dataKey="completed" name="Completed" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="target" name="Target" fill="var(--color-muted)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Exercise Type Donut */}
      {typeData?.length > 0 && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Exercise Type Breakdown</h3>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={typeData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                  {typeData?.map((_, idx) => (
                    <Cell key={idx} fill={EXERCISE_COLORS?.[idx % EXERCISE_COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-caption)' }}
                />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12, fontFamily: 'var(--font-caption)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
